// import {isEqual} from "lodash";

const EPSILON = 1000 * Number.EPSILON;

export type Interval = [number, number];
export type HeightInterval = [number, number | null];

/**
 * Funktionen zum Umgang mit abgeschlossenen Intervallen reeler Zahlen. Ein Interval ist dabei ein Array,
 * [0,1] steht also für das Interval von 0 bis 1.
 */

/**
 * prüft, ob zwei Intervalle sich schneiden
 * @param {Array} a
 * @param {Array} b
 * @returns {boolean}
 */
export function intersects(a: Interval, b: Interval): boolean {
  return !(a[1] < b[0] || a[0] > b[1]);
}

/**
 * gibt die Vereinigung zweier Intervalle zurück
 * @param {?Array} a
 * @param {?Array} b
 * @returns {?Array}
 */
export function union(a: Interval | null, b: Interval | null): Interval | null {
  if (a === null || b === null) {
    return a == null ? b : a;
  }
  return [Math.min(a[0], b[0]), Math.max(a[1], b[1])];
}

/**
 * gibt die Schnittmenge zweier Intervalle zurück
 * @param {?Array} a
 * @param {?Array} b
 * @returns {?Array}
 */
export function intersection(a: Interval | null, b: Interval | null): Interval | null {
  if (a === null || b === null) {
    return a == null ? b : a;
  }
  if (intersects(a, b)) {
    const l = Math.max(a[0], b[0]);
    const r = Math.min(a[1], b[1]);
    if (l <= r) {
      return [l, r];
    }
  }
  return null;
}

/**
 * gibt die Schnittintervalle zweier Intervall-Mengen zurück. Ist O(n^2).
 * @param aIntervals
 * @param bIntervals
 * @param epsilon
 * @returns {Array<Array>}
 */
export function intersections(
  aIntervals: Interval[],
  bIntervals: Interval[],
  epsilon: number = EPSILON,
): Interval[] {
  const intersections: Interval[] = [];
  // TODO das hier hat O(n^2), was auch als O(n) geht, aber mir fehlt grad die Zeit, das sauber hinzukriegen...
  aIntervals.forEach((a) =>
    bIntervals.forEach((b) => {
      const i = intersection(a, b);
      if (i) {
        intersections.push(i);
      }
    }),
  );
  return maybeUnion(intersections, epsilon);
}

/**
 * gibt die Schnittmenge zweier Intervalle zurück, die sortiert sind. Ist O(n), setzt aber vorraus,
 * dass beide Mengen sortiert sind. Achtung: Ungetestetet GPT-4-Code!
 * @param {?Array} a
 * @param {?Array} b
 * @returns {?Array}
 */
export function intersectionsSorted(
  aIntervals: Interval[],
  bIntervals: Interval[],
  epsilon: number = EPSILON,
): Interval[] {
  const intersections: Interval[] = [];
  let aIndex = 0;
  let bIndex = 0;

  while (aIndex < aIntervals.length && bIndex < bIntervals.length) {
    const a = aIntervals[aIndex];
    const b = bIntervals[bIndex];
    const i = intersection(a, b);
    if (i) {
      intersections.push(i);
    }
    if (a[1] < b[1]) {
      aIndex++;
    } else {
      bIndex++;
    }
  }
  return maybeUnion(intersections, epsilon);
}

/**
 * sortiert eine Intervall-Mennge
 * @param intervals
 * @returns {*}
 */
export function sort(intervals: Interval[]): Interval[] {
  return intervals.sort((a, b) => a[0] - b[0]);
}

/**
 * vergrössert ein Interval a um einen festen Betrag x sowohl nach links als auch rechts
 * @param {?Array} a
 * @param {number} x
 * @returns {?Array}
 */
export function expandBy(a: Interval | null, x: number): Interval | null {
  if (a === null) {
    return null;
  }
  return [a[0] - x, a[1] + x];
}

/**
 * gibt die Länge eines Intervals zurück
 * @param {?Array} a
 */
export function size(a: Interval | null): number {
  return a === null ? 0 : a[1] - a[0];
}

/**
 * @param intervals
 * @return {Array}
 * @param {...Array<Array>} intervals
 */
export function clone(...intervals: Interval[]): Interval[] {
  return intervals.map((i) => [i[0], i[1]]);
}

/**
 * sortiert die Grenzen des Intervals, so dass garantiert a[0]<=a[1] ist
 * @param {Array} a
 */
export function order(a: Interval): Interval {
  const l = Math.min(a[0], a[1]);
  const r = Math.max(a[0], a[1]);
  a[0] = l;
  a[1] = r;
  return a;
}

/**
 * Sortiert eine Liste von Intervallen und stellt sicher, dass bei jedem Intervall das erste Element kleiner
 * als das zweite Element ist, und entfernt Intervalle mit NaN.
 * @param {Array<Array>} intervals
 * @returns {Array<Array>}
 */
export function shapely(intervals: Interval[]): Interval[] {
  intervals = intervals.filter((i) => !(Number.isNaN(i[0]) || Number.isNaN(i[1])));
  intervals.forEach((i) => {
    if (i[0] > i[1]) {
      const h = i[0];
      i[0] = i[1];
      i[1] = h;
    }
  });
  return sort(intervals);
}

/**
 * Vereinigt alle sich schneidenden Intervalle
 * @param {Array<Array>} intervals
 * @param {number} [epsilon=0.0000001]
 * @returns {Array<Array>}
 */
export function maybeUnion(intervals: Interval[], epsilon: number = EPSILON): Interval[] {
  let last: Interval | null = null;
  const result: Interval[] = [];
  for (let cur of shapely(intervals)) {
    if (last == null || last[1] < cur[0] - epsilon) {
      last = [cur[0], cur[1]];
      result.push(last);
    } else {
      if (last[1] < cur[1] + epsilon) {
        last[1] = cur[1];
      }
    }
  }
  return result;
}

/**
 * Entfernt Punkt-Intervalle (kleiner epsilon)
 * @param {Array<Array>} intervals
 * @param {number} [epsilon=0.0000001]
 * @returns {Array<Array>}
 */
export function removePoints(intervals: Interval[], epsilon: number = EPSILON): Interval[] {
  const result: Interval[] = [];
  for (let cur of shapely(intervals)) {
    if (cur[1] - cur[0] > epsilon) {
      result.push(cur);
    }
  }
  return result;
}

/**
 * ist eine Zahl im Interval enthalten?
 * @param {[]} interval
 * @param {number} point
 * @returns {boolean}
 */
export function containsPoint(interval: Interval, point: number): boolean {
  return point >= interval[0] && point <= interval[1];
}

/**
 * prüft, ob Intervall a das Intervall b enthält
 * @param {Array} a
 * @param {Array} b
 * @param {number} epsilon
 * @returns {boolean}
 */
export function containsInterval(a: Interval, b: Interval, epsilon: number = EPSILON): boolean {
  return a[0] - epsilon <= b[0] && b[1] <= a[1] + epsilon;
}

/**
 * schneidet ein Intervall aus einer Liste von Intervallen aus
 * @param intervals
 * @param b
 * @returns {*[]}
 */
export function subtract(intervals: Interval[], b: Interval): Interval[] {
  const newIntervals: Interval[] = [];
  for (const a of intervals) {
    if (a[1] < b[0] || a[0] > b[1]) {
      newIntervals.push(a);
    } else if (a[0] >= b[0] && a[1] <= b[1]) {
      // remove a
    } else {
      if (a[0] < b[0]) {
        newIntervals.push([a[0], b[0]]);
      }
      if (a[1] > b[1]) {
        newIntervals.push([b[1], a[1]]);
      }
    }
  }
  return newIntervals;
}

/**
 * Fügt einer Liste von HeightIntervals ein neues HeightInterval hinzu.
 * Ein HeightInterval ist ein Interval, das zusätzlich eine Höhe hat.
 * Höhere Intervalle verdecken niedrigere Intervalle.
 * HeightIntervalle sind eine andere Datenstruktur als die obigen normalen Intervalle:
 * [[x0,h1],[x1,h2],[x2,h3],...]
 * D.h. angegeben ist immer eine Intervallgrenze und die Höhe ab dieser Grenze,
 * und die x-Koordinaten sind aufsteigend sortiert. Das erste Interval x0 bis x1
 * hat also die Höhe h1. Wenn die Höhe null ist, dann gilt das Intervall als nicht gesetzt.
 * @param {Array<[number,number]>} intervals Ein Array von HeightIntervals
 * @param l linke Intervallgrenze
 * @param r rechte Intervallgrenze
 * @param h Höhe des Intervals
 * @param epsilon
 * @returns {*[][]|*[]} ein neues Array von HeightIntervals
 */
export function insertHeightInterval(
  intervals: HeightInterval[],
  l: number,
  r: number,
  h: number,
  epsilon: number = EPSILON,
): HeightInterval[] {
  if (intervals.length == 0) {
    return [
      [l, h],
      [r, null],
    ];
  }
  const START = Symbol();
  const STOP = Symbol();

  function* next(): Generator<[number, number | null | symbol], void, unknown> {
    let lyield = false;
    let ryield = false;
    for (const i of intervals) {
      if (!lyield && l < i[0]) {
        lyield = true;
        yield [l, START];
      }
      if (!ryield && r < i[0]) {
        ryield = true;
        yield [r, STOP];
      }
      yield i;
    }
    if (!lyield) {
      yield [l, START];
    }
    if (!ryield) {
      yield [r, STOP];
    }
  }

  let last: HeightInterval | undefined = undefined;
  const newIntervals: HeightInterval[] = [];
  let inside = false;
  const add = (x: number, nh: number | null) => {
    if (last === undefined) {
      last = [x, nh];
      newIntervals.push(last);
    } else {
      const [lx, lh] = last;
      if (lh != nh && (nh == null || lh == null || Math.abs(lh - nh) > epsilon)) {
        // Note: original had (nh == null || Math.abs(lh - nh) > epsilon), which assumed lh not null if nh not null?
        // or Math.abs handling null?
        // JS Math.abs(null - 5) -> 5. Math.abs(5 - null) -> 5. Math.abs(null - null) -> 0.
        // so null was treated as 0?
        // But HeightInterval doc says "Wenn die Höhe null ist, dann gilt das Intervall als nicht gesetzt."
        // We should likely preserve logic.
        // Original: if (lh != nh && (nh == null || Math.abs(lh - nh) > epsilon))
        // If lh was null, Math.abs(null - nh).
        // If nh was null, short circuit.
        // I will preserve original logic but cast to any to be safe or explicit number conversion if needed for TS.
        // Actually, if lh or nh is null, we can't subtract.
        // But in JS Math.abs(null) is 0.
        // I will check if lh is null.
        if (lh != nh) {
          let diff = 0;
          if (nh != null && lh != null) diff = Math.abs(lh - nh);
          else if (nh != null || lh != null)
            diff = Infinity; // different!
          else diff = 0; // both null

          // Wait, original JS behavior:
          // Math.abs(5 - null) = 5.
          // Math.abs(null - 5) = 5.
          // So if one is null, diff is the value of the other.
          // If both null, diff is 0.
          // The condition `nh == null || Math.abs(...) > epsilon` meant:
          // if nh is null (and lh != nh, so lh not null), then TRUE.
          // if nh is not null, then check diff.
          // if lh is null (and nh not null), then abs(0 - nh) = nh. If nh > epsilon, then TRUE.

          const lhVal = lh ?? 0;
          const nhVal = nh ?? 0;
          const diffVal = Math.abs(lhVal - nhVal);

          // Original: (nh == null || Math.abs(lh - nh) > epsilon)
          // I will stick to exact TS equivalent of JS behavior if possible, or fix.
          // To be TS strict, I cannot subtract nulls.
          // I'll leave a comment and implement safe check.
          if (
            nh === null ||
            (lh !== null && Math.abs(lh - nh) > epsilon) ||
            (lh === null && Math.abs(nh) > epsilon)
          ) {
            if (Math.abs(lx - x) < epsilon) {
              last[1] = nh;
            } else {
              last = [x, nh];
              newIntervals.push(last);
            }
          }
        }
      }
    }
  };
  let lxh: number | null = null;
  for (const [x, xh] of next()) {
    if (xh === START) {
      inside = true;
      const nh = h == null || lxh == null ? h : Math.max(lxh, h);
      add(x, nh);
    } else if (xh === STOP) {
      inside = false;
      add(x, lxh);
    } else {
      // xh is number | null
      const realXh = xh as number | null;
      if (inside) {
        const nh = h == null || realXh == null ? h : Math.max(realXh, h);
        add(x, nh);
      } else {
        add(x, realXh);
      }
      lxh = realXh;
    }
  }
  return newIntervals;
}

/**
 * Filtere aus einer Liste von HeightIntervals alle, welche die width- und height-Bedingungen erfüllen.
 * @param {Array<[number,number]>} intervals Ein Array von HeightIntervals
 * @param {(number)=>boolean} widthMatches
 * @param {(number)=>boolean} heightMatches
 * @returns {*[][]|*[]} ein neues Array von HeightIntervals
 */
export function* filterHeightInterval(
  intervals: HeightInterval[],
  widthMatches: (w: number) => boolean = (w) => true,
  heightMatches: (h: number) => boolean = (h) => true,
): Generator<Interval, void, unknown> {
  let left = -1;
  for (let i = 0; i < intervals.length; i++) {
    const height = intervals[i][1];
    if (left === -1) {
      if (height !== null && heightMatches(height)) {
        left = i;
      }
    } else {
      if (height == null || !heightMatches(height)) {
        const l = intervals[left][0];
        const r = intervals[i][0];
        const width = r - l;
        if (widthMatches(width)) {
          yield [l, r];
        }
        left = -1;
      }
    }
  }
}

/**
 * Prüft, ob ein Intervall in die Liste von HeightIntervals passt, also ob die Höhe der
 * vorhandenen Intervalle im Bereich l-r mindestens die Höhe h erreicht
 * @param {Array<[number,number]>} intervals Ein Array von HeightIntervals
 * @param l linke Intervallgrenze
 * @param r rechte Intervallgrenze
 * @param h Höhe des Intervals
 * @returns {boolean}
 */
export function heightIntervalFits(
  intervals: HeightInterval[],
  l: number,
  r: number,
  h: number,
): boolean {
  if (intervals.length == 0) {
    return true;
  }
  let lPass = false;
  let rPass = false;
  let oh: number | null = null;
  for (const [x, xh] of intervals) {
    if (!lPass && l < x) {
      if (oh != null && h < oh) {
        return false;
      }
      lPass = true;
    }
    if (!rPass && r < x) {
      if (oh != null && h < oh) {
        return false;
      }
      rPass = true;
    }
    if (lPass && !rPass) {
      if (xh != null && h < xh) {
        return false;
      }
    }
    oh = xh;
  }
  return true;
}

export function distance(point: number, interval: Interval): number {
  if (point < interval[0]) {
    return interval[0] - point;
  } else if (point > interval[1]) {
    return point - interval[1];
  } else {
    return 0;
  }
}

export function middle(interval: Interval): number {
  return (interval[0] + interval[1]) / 2;
}

/**
 * gibt die Länge der Intersection zurück
 * @param {number[]} a
 * @param {number[]} b
 * @returns {number}
 */
export function getOverlap(a: Interval, b: Interval): number {
  return size(intersection(a, b));
}

/**
 * gibt die maximale Länge des Überhanges von Intervall a über Intervall b zurück.
 * @param a
 * @param b
 * @returns {number}
 */
export function getOverhang(a: Interval, b: Interval): number {
  const [aStart, aEnd] = a;
  const [bStart, bEnd] = b;
  return Math.max(0, aStart - bStart, bEnd - aEnd);
}

/**
 * Extrapolierte Intervalle berechnen (mutable):
 * - Das erste persistente Intervall wird nach links bis -Infinity extrapoliert.
 * - Das letzte bis +Infinity.
 * - Benachbarte Intervallen werden auf den Mittelpunkt der Grenzen gesetzt
 */
export function extrapolateInfiniteAndMiddles(intervals: Interval[]): void {
  sort(intervals);
  for (let i = 0; i < intervals.length; i++) {
    const prev = intervals[i];
    if (i === 0) {
      prev[0] = -Infinity;
    }
    if (i === intervals.length - 1) {
      prev[1] = Infinity;
    } else {
      const next = intervals[i + 1];
      prev[1] = next[0] = (prev[1] + next[0]) / 2;
    }
  }
}

(globalThis as any).insertHeightInterval = insertHeightInterval;
